import React, { useContext } from 'react'
import { Radio } from 'antd'
import { ColorPicker } from '../../../../../components/color-picker/color-picker'
import './slide-prompt-theme-editor.scss'
import { FileUploader, FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { simpleNotification } from '../../../../../_utils/utils'
import {
    TemplatizedTextFieldWithColor,
    TextWithColorValues,
    TextWithForegroundColorValue,
} from '../../../../../components/templatized-text-field-with-color/templatized-text-field-with-color'
import { PromptButtonConfig, PromptDevicePosition, SlidePromptTheme } from '@pushly/models/lib/structs'
import { PromptTextConfig } from '@pushly/models/lib/structs/prompts/prompt-text-config'
import {
    DEFAULT_ALLOW_BUTTON_COLOR,
    DEFAULT_ALLOW_TEXT,
    DEFAULT_ALLOW_TEXT_COLOR,
    DEFAULT_BACKGROUND_COLOR,
    DEFAULT_DESKTOP_POSITION,
    DEFAULT_DISMISS_BUTTON_COLOR,
    DEFAULT_DISMISS_TEXT,
    DEFAULT_DISMISS_TEXT_COLOR,
    DEFAULT_LAYOUT,
    DEFAULT_MOBILE_POSITION,
    DEFAULT_SUBTITLE_COLOR,
    DEFAULT_TITLE_COLOR,
} from '../../constants'
import { PromptEditorContext } from '../../context'

// Uses props to guarantee a specific style of Prompt
type SlidePromptDataFormProps = {
    value?: SlidePromptTheme
    onChange: (theme: SlidePromptTheme) => void
}

export const SlidePromptThemeEditor: React.FC<SlidePromptDataFormProps> = ({ value, onChange }) => {
    const { loading, domain } = useContext(PromptEditorContext)

    const theme = value ?? new SlidePromptTheme()

    const setTheme = (setter: (theme: SlidePromptTheme) => SlidePromptTheme) => {
        onChange(setter(theme.clone()))
    }

    return (
        <Well className="customizations-well" title="Theme" hideFooter={true} mode="ghost">
            <FormLayoutRow
                loading={loading}
                className="prompt-slide-layout"
                label="Slide Layout"
                value={
                    !loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.layout ?? DEFAULT_LAYOUT}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.layout = ev.target.value
                                    return update
                                })
                            }}
                        >
                            <Radio.Button value="normal">Horizontal</Radio.Button>
                            <Radio.Button value="stacked">Vertical</Radio.Button>
                        </Radio.Group>
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                className="prompt-desktop-pos"
                label="Desktop Position"
                value={
                    !loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.position.desktop ?? DEFAULT_DESKTOP_POSITION}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.position = PromptDevicePosition.from({
                                        desktop: ev.target.value,
                                        mobile: update.position?.mobile ?? DEFAULT_MOBILE_POSITION,
                                    })
                                    return update
                                })
                            }}
                        >
                            <Radio.Button value="top">Top</Radio.Button>
                            <Radio.Button value="bottom">Bottom</Radio.Button>
                        </Radio.Group>
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                className="prompt-mobile-pos"
                label="Mobile Position"
                value={
                    !loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.position.mobile ?? DEFAULT_MOBILE_POSITION}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.position = PromptDevicePosition.from({
                                        mobile: ev.target.value,
                                        desktop: update.position?.desktop ?? DEFAULT_DESKTOP_POSITION,
                                    })
                                    return update
                                })
                            }}
                        >
                            <Radio.Button value="top">Top</Radio.Button>
                            <Radio.Button value="bottom">Bottom</Radio.Button>
                        </Radio.Group>
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                layout="vertical"
                label="Title"
                value={
                    !loading && (
                        <TemplatizedTextFieldWithColor<TextWithForegroundColorValue>
                            placeholder="Title"
                            hideMacros={true}
                            value={
                                !prompt
                                    ? undefined
                                    : {
                                          value: theme.title?.text,
                                          fgColor: theme.title?.textColor ?? DEFAULT_TITLE_COLOR,
                                      }
                            }
                            onChange={(nextValue) => {
                                setTheme((update) => {
                                    update.title = PromptTextConfig.from({
                                        text: nextValue.value!,
                                        textColor: nextValue.fgColor!,
                                    })
                                    return update
                                })
                            }}
                        />
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                layout="vertical"
                label="Subtitle"
                value={
                    !loading && (
                        <TemplatizedTextFieldWithColor<TextWithForegroundColorValue>
                            placeholder="Subtitle"
                            hideMacros={true}
                            value={
                                !prompt
                                    ? undefined
                                    : {
                                          value: theme.subtitle?.text,
                                          fgColor: theme.subtitle?.textColor ?? DEFAULT_SUBTITLE_COLOR,
                                      }
                            }
                            onChange={(nextValue) => {
                                setTheme((update) => {
                                    update.subtitle = PromptTextConfig.from({
                                        text: nextValue.value!,
                                        textColor: nextValue.fgColor!,
                                    })
                                    return update
                                })
                            }}
                        />
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                layout="vertical"
                label="Allow Button"
                value={
                    !loading && (
                        <TemplatizedTextFieldWithColor<TextWithColorValues>
                            placeholder="Allow"
                            hideMacros={true}
                            showBgColorOption={true}
                            value={
                                !prompt
                                    ? undefined
                                    : {
                                          value: theme.allowButton?.text ?? DEFAULT_ALLOW_TEXT,
                                          fgColor: theme.allowButton?.textColor ?? DEFAULT_ALLOW_TEXT_COLOR,
                                          bgColor: theme.allowButton?.backgroundColor ?? DEFAULT_ALLOW_BUTTON_COLOR,
                                      }
                            }
                            onChange={(nextValue) => {
                                setTheme((update) => {
                                    update.allowButton = PromptButtonConfig.from({
                                        text: nextValue.value!,
                                        textColor: nextValue.fgColor!,
                                        backgroundColor: nextValue.bgColor!,
                                    })
                                    return update
                                })
                            }}
                        />
                    )
                }
            />

            <FormLayoutRow
                loading={loading}
                layout="vertical"
                label="Dismiss Button"
                value={
                    !loading && (
                        <TemplatizedTextFieldWithColor<TextWithColorValues>
                            placeholder="Dismiss"
                            hideMacros={true}
                            showBgColorOption={true}
                            value={
                                !prompt
                                    ? undefined
                                    : ({
                                          value: theme.dismissButton?.text ?? DEFAULT_DISMISS_TEXT,
                                          fgColor: theme.dismissButton?.textColor ?? DEFAULT_DISMISS_TEXT_COLOR,
                                          bgColor: theme.dismissButton?.backgroundColor ?? DEFAULT_DISMISS_BUTTON_COLOR,
                                      } satisfies TextWithColorValues)
                            }
                            onChange={(nextValue) => {
                                setTheme((update) => {
                                    update.dismissButton = PromptButtonConfig.from({
                                        text: nextValue.value!,
                                        textColor: nextValue.fgColor!,
                                        backgroundColor: nextValue.bgColor!,
                                    })
                                    return update
                                })
                            }}
                        />
                    )
                }
            />

            <div className="customization-lower">
                <FormLayoutRow
                    className="prompt-bg-color"
                    loading={loading}
                    layout="vertical"
                    label="Background Color"
                    value={
                        !loading && (
                            <ColorPicker.Button
                                className="prompt-background-picker"
                                value={theme.backgroundColor ?? DEFAULT_BACKGROUND_COLOR}
                                onChange={(nextValue: string) => {
                                    setTheme((update) => {
                                        update.backgroundColor = nextValue
                                        return update
                                    })
                                }}
                            />
                        )
                    }
                />

                <FormLayoutRow
                    className="prompt-image-upload"
                    loading={loading}
                    layout="vertical"
                    label="Image"
                    value={
                        !loading && (
                            <FileUploader
                                className="image-upload"
                                type="image"
                                ownerType="domain"
                                ownerId={domain.id}
                                imageConfig={{ quality: domain.mediaQuality }}
                                value={!theme.image ? undefined : { url: theme.image }}
                                onSuccess={(img) => {
                                    setTheme((update) => {
                                        update.image = img.url
                                        return update
                                    })
                                }}
                                onRemove={() => {
                                    setTheme((update) => {
                                        update.image = null!
                                        return update
                                    })
                                }}
                                onError={(res: any) => {
                                    const error = res?.error
                                    if (error && error.error_type === 'invalid_file_type_error') {
                                        return simpleNotification('error', error.message)
                                    }
                                }}
                            />
                        )
                    }
                />
            </div>
        </Well>
    )
}
